import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOpsCapacity } from './useOpsCapacity';
import {
  fmtDayLong,
  fmtInt,
  fmtLondonInstant,
  fmtMinutes,
  fmtPct,
  isIsoDate,
  KIND_LABEL,
  KINDS,
  londonDateOf,
  londonToday,
  OPS_TIMEZONE,
  RANGE_PROBLEM_TEXT,
  validateRange,
} from './opsCapacityUtils';
import { Section } from './OpsCapacitySection';
import { CaptchaSection } from './OpsCapacityCaptchaSection';
import {
  DailyDemandChart,
  DemandHeatmap,
  HourProfileChart,
  WeekdayProfileChart,
  WeeklyDemandChart,
} from './OpsCapacityDemandCharts';
import {
  ConcentrationSection,
  CoverageSection,
  LanguagesSection,
  QualitySection,
  SpeedSection,
} from './OpsCapacityDetailSections';
import { ProjectionsSection } from './OpsCapacityProjections';
import type { OpsCapacityResponse } from './types';
import './ops-capacity-dashboard.scss';

export const FROM_PARAM = 'from';
export const TO_PARAM = 'to';
/** Settle time for typed picker edits before the range is committed. */
export const COMMIT_DELAY_MS = 600;

const daysBack = (today: string, n: number) => {
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
};

const PRESETS: Array<{ label: string; days: number | null }> = [
  { label: 'All time', days: null },
  { label: '7d', days: 6 },
  { label: '30d', days: 29 },
  { label: '90d', days: 89 },
];

/**
 * Ops Capacity tab: operator demand vs capacity for an inclusive London date
 * range. The committed range lives in the URL (`?from=&to=`) so a view can be
 * shared; the inputs hold a draft that is only committed when it is valid, so
 * half-typed dates never fire a request or blank the charts.
 */
const OpsCapacityDashboard: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFrom = searchParams.get(FROM_PARAM);
  const urlTo = searchParams.get(TO_PARAM);
  const committed = useMemo(
    () => ({
      from: isIsoDate(urlFrom) ? urlFrom : undefined,
      to: isIsoDate(urlTo) ? urlTo : undefined,
    }),
    [urlFrom, urlTo],
  );

  const { data, loading, stale, error, forbidden, refetch } =
    useOpsCapacity(committed);

  const today = useMemo(() => londonToday(), []);
  const [draftFrom, setDraftFrom] = useState(committed.from ?? '');
  const [draftTo, setDraftTo] = useState(committed.to ?? today);

  // Once the server resolves the defaults, show them in the inputs.
  useEffect(() => {
    if (!data) return;
    setDraftFrom(committed.from ?? data.range.startDate);
    setDraftTo(committed.to ?? data.range.endDate);
  }, [data, committed.from, committed.to]);

  const commit = useCallback(
    (from: string, to: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(FROM_PARAM, from);
          next.set(TO_PARAM, to);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetToDefault = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(FROM_PARAM);
        next.delete(TO_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Only validate once the user has touched the pickers; before the server
  // default arrives the From box is legitimately empty.
  const [touched, setTouched] = useState(false);
  const problem = touched ? validateRange(draftFrom, draftTo, today) : null;

  // Native date inputs fire `change` for every intermediate valid value while
  // a date is typed, so picker edits settle briefly before they are committed;
  // presets commit at once.
  const pendingCommit = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => () => clearTimeout(pendingCommit.current), []);

  const onDraft = (from: string, to: string, immediate = false) => {
    setTouched(true);
    setDraftFrom(from);
    setDraftTo(to);
    clearTimeout(pendingCommit.current);
    if (validateRange(from, to, today) !== null) return;
    if (immediate) commit(from, to);
    else
      pendingCommit.current = setTimeout(
        () => commit(from, to),
        COMMIT_DELAY_MS,
      );
  };

  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const isDefaultRange = committed.from == null && committed.to == null;

  if (forbidden) {
    return (
      <div className='ops-capacity'>
        <div className='msg error'>
          You need System Manager access to view Ops Capacity.
        </div>
      </div>
    );
  }

  return (
    <div className='ops-capacity'>
      <div className='ops-toolbar'>
        <div className='ops-range'>
          <label className='ops-range-field'>
            <span>From</span>
            <input
              type='date'
              className='date-input'
              value={draftFrom}
              onChange={(e) => onDraft(e.target.value, draftTo)}
              aria-label='From date (Europe/London)'
            />
          </label>
          <span className='range-arrow'>→</span>
          <label className='ops-range-field'>
            <span>To</span>
            <input
              type='date'
              className='date-input'
              value={draftTo}
              onChange={(e) => onDraft(draftFrom, e.target.value)}
              aria-label='To date (Europe/London)'
            />
          </label>
          <div className='day-selector'>
            {PRESETS.map((p) => {
              const active =
                p.days == null
                  ? isDefaultRange
                  : committed.from === daysBack(today, p.days) &&
                    committed.to === today;
              return (
                <button
                  key={p.label}
                  type='button'
                  className={`day-btn ${active ? 'active' : ''}`}
                  onClick={() =>
                    p.days == null
                      ? resetToDefault()
                      : onDraft(daysBack(today, p.days), today, true)
                  }
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <button type='button' className='ops-btn-link' onClick={copyLink}>
            {copied ? 'Link copied' : 'Copy link to this view'}
          </button>
        </div>
        <p className='ops-tz-note'>
          All dates, hours and shifts are <strong>{OPS_TIMEZONE}</strong> (the
          operator rota&apos;s timezone), not your browser&apos;s. Today in
          London is {fmtDayLong(today)}
          {data?.range.includesToday && (
            <>
              {' '}
              —{' '}
              <span className='ops-badge ops-badge--warn'>
                today is partial
              </span>
            </>
          )}
          .
        </p>
      </div>

      {problem && (
        <div className='msg error' role='alert'>
          {RANGE_PROBLEM_TEXT[problem]}
        </div>
      )}

      {error && (
        <div className='msg error' role='alert'>
          {error}{' '}
          <button type='button' className='ops-btn-link' onClick={refetch}>
            Retry
          </button>
        </div>
      )}

      {loading && !data && (
        <div className='loading' role='status'>
          Loading ops capacity…
        </div>
      )}

      {data && (
        <div
          className={`ops-body ${loading && stale ? 'ops-body--stale' : ''}`}
          aria-busy={loading}
        >
          {loading && stale && (
            <div className='ops-updating' role='status'>
              Updating…
            </div>
          )}
          <Body data={data} today={today} />
        </div>
      )}
    </div>
  );
};

const Body: FC<{ data: OpsCapacityResponse; today: string }> = ({
  data,
  today,
}) => {
  const { range, kpis, incidents, definitions } = data;
  const totalDemand = KINDS.reduce((s, k) => s + kpis[k], 0);
  const rangeLabel = `${fmtDayLong(range.startDate)} – ${fmtDayLong(range.endDate)}`;

  const visibleIncidents = incidents.filter((i) => {
    const s = londonDateOf(i.start);
    const e = londonDateOf(i.end);
    return s <= range.endDate && e >= range.startDate;
  });

  if (totalDemand === 0 && kpis.otherTasks === 0) {
    return (
      <p className='ops-empty ops-empty--page'>
        No operator demand recorded between {rangeLabel}
        {range.includesToday ? ' (today so far)' : ''}. Earliest data is{' '}
        {fmtDayLong(range.earliestDate)}.
      </p>
    );
  }

  return (
    <>
      <p className='ops-range-summary'>
        Showing <strong>{rangeLabel}</strong> · {range.days} day
        {range.days === 1 ? '' : 's'} · data from{' '}
        {fmtDayLong(range.earliestDate)}
        {data.cached ? ' · cached' : ` · ${data.queryMs} ms`}
      </p>

      <div className='ops-kpis' aria-label='Key figures'>
        {KINDS.map((k) => (
          <Kpi key={k} label={KIND_LABEL[k]} value={fmtInt(kpis[k])} kind={k} />
        ))}
        <Kpi
          label='Other tasks'
          value={fmtInt(kpis.otherTasks)}
          sub='help / in-person'
        />
        <Kpi
          label='Requesters'
          value={fmtInt(kpis.requesters)}
          sub='distinct users'
        />
        <Kpi
          label='WAU (last full week)'
          value={fmtInt(kpis.latestWeekWau)}
          sub='in range'
        />
        <Kpi
          label='Pickup wait'
          value={fmtMinutes(kpis.pickupMedianMinutes)}
          sub={`p90 ${fmtMinutes(kpis.pickupP90Minutes)}`}
        />
        <Kpi
          label='Arrived in a rostered shift'
          value={fmtPct(kpis.inRotaSharePct)}
          sub='of tasks'
        />
      </div>

      {visibleIncidents.length > 0 && (
        <div className='ops-incidents' role='note'>
          <strong>Incidents in this range</strong> (shaded on the time charts):
          <ul>
            {visibleIncidents.map((i) => (
              <li key={i.start}>
                <span className='ops-badge ops-badge--incident'>{i.label}</span>{' '}
                {fmtLondonInstant(i.start)} → {fmtLondonInstant(i.end)} London.{' '}
                {i.note}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Section
        id='weekly'
        title='Weekly demand by stream'
        intro='Phone, web, interventions and MFA are counted separately; the line is weekly active users.'
        definition={definitions.volume}
      >
        <WeeklyDemandChart weekly={data.weekly} incidents={visibleIncidents} />
      </Section>

      <Section
        id='daily'
        title='Daily demand'
        intro='Every London calendar day in the range, including zero days.'
        definition={definitions.range}
      >
        <DailyDemandChart
          daily={data.daily}
          incidents={visibleIncidents}
          today={today}
        />
      </Section>

      <div className='ops-grid ops-grid--2'>
        <Section id='weekday' title='By weekday' definition={definitions.range}>
          <WeekdayProfileChart rows={data.weekdayProfile} />
        </Section>
        <Section
          id='hour'
          title='By hour of day (London)'
          definition={definitions.range}
        >
          <HourProfileChart rows={data.hourProfile} />
        </Section>
      </div>

      <Section
        id='heatmap'
        title='When demand lands: weekday × hour'
        intro='Darker is busier. Use it to place shifts.'
      >
        <DemandHeatmap cells={data.heatmap} />
      </Section>

      <Section
        id='languages'
        title='Language mix (phone tasks)'
        definition={definitions.languages}
      >
        <LanguagesSection languages={data.languages} />
      </Section>

      <Section
        id='coverage'
        title='Rota coverage by shift'
        definition={definitions.coverage}
      >
        <CoverageSection coverage={data.coverage} />
      </Section>

      <Section id='speed' title='Speed' definition={definitions.speed}>
        <SpeedSection speed={data.speed} />
      </Section>

      <Section id='quality' title='Outcomes' definition={definitions.quality}>
        <QualitySection quality={data.quality} />
      </Section>

      <Section
        id='concentration'
        title='Who creates the work, who does it'
        definition={definitions.concentration}
      >
        <ConcentrationSection concentration={data.concentration} />
      </Section>

      {data.captcha && (
        <Section
          id='captcha'
          title='CAPTCHA demand'
          intro='Classified historical interventions and web actions, alongside the newer solver events.'
          definition={definitions.captcha}
        >
          <CaptchaSection
            captcha={data.captcha}
            daily={data.daily}
            today={today}
          />
        </Section>
      )}

      <Section
        id='projections'
        title='Staffing projections'
        intro='Rates from this range × target WAU. Edit the assumptions to test your own.'
        definition={definitions.projections}
      >
        <ProjectionsSection rates={data.rates} defaults={data.assumptions} />
      </Section>
    </>
  );
};

const Kpi: FC<{
  label: string;
  value: string;
  sub?: string;
  kind?: (typeof KINDS)[number];
}> = ({ label, value, sub, kind }) => (
  <div className={`ops-kpi ${kind ? `ops-kpi--${kind}` : ''}`}>
    <span className='ops-kpi-label'>{label}</span>
    <span className='ops-kpi-value'>{value}</span>
    {sub && <span className='ops-kpi-sub'>{sub}</span>}
  </div>
);

export default OpsCapacityDashboard;
